import { createServerFn } from "@tanstack/react-start"
import { eq } from "drizzle-orm"
import { pick, sortBy } from "lodash-es"
import { z } from "zod"

import { dataByFileRequestStatus } from "@/constants/terminal/file-request-status"
import { PortfolioCompanyStatus } from "@/constants/terminal/portfolio-company-status"
import {
  readCompanyFile,
  readPortfolioFeed,
  readPortfolioFiles,
  readPortfolioRows,
} from "@/db/queries/terminal-file-reader"
import { readFileRequests } from "@/db/queries/terminal-file-requests"
import {
  getPortfolioDelivery,
  setPortfolioEntryMonitoring,
} from "@/db/queries/terminal-portfolio"
import { portfolio, portfolioUser } from "@/db/schema/public-terminal"
import { terminalFileRequest } from "@/db/schema/terminal-portfolio"
import { db } from "@/lib/admin-db"
import {
  resolvePlaceStreetAddress,
  suggestPlaces,
} from "@/lib/google-places-client"
import { assertPortfolioAccess, resolveAuthContext } from "@/lib/supabase/auth"
import { buildExportRows } from "@/lib/terminal/file-export"
import {
  approvedOnSchema,
  fileRequestFieldsSchema,
  type TerminalFileRequest,
} from "@/lib/terminal/file-request"
import type {
  TerminalFeedDetection,
  TerminalFileView,
  TerminalPortfolioOption,
  TerminalPortfolioRow,
} from "@/lib/terminal/file-view"
import { NIGHTSHIFT_ORIGIN } from "@/lib/terminal/nightshift-domain"

// The customer Terminal's server functions. A `createServerFn` endpoint is
// directly callable, so every handler first calls `assertPortfolioAccess()`
// with the Portfolio it names, the way admin handlers call
// `assertAdminContext()`: it throws unless the caller holds a `portfolio_user`
// row for that Portfolio or is an admin. A Company is readable only through a
// Current Portfolio Entry of that Portfolio, and only the File that entry
// delivers is served: the Company's File now for a monitored entry, as of its
// pinned time for a snapshot.

const portfolioInput = z.object({ portfolioSlug: z.string().min(1) })
const companyFileInput = portfolioInput.extend({ slug: z.string().min(1) })

const portfolioOption = {
  id: portfolio.id,
  name: portfolio.name,
  slug: portfolio.slug,
}

// The Portfolios the caller may open, for the switcher and the default.
export const loadTerminalPortfolios = createServerFn({ method: "GET" }).handler(
  async (): Promise<TerminalPortfolioOption[]> => {
    const { user, userType } = await resolveAuthContext()
    if (!user) return []
    if (userType === "admin")
      return db.select(portfolioOption).from(portfolio).orderBy(portfolio.name)
    return db
      .select(portfolioOption)
      .from(portfolioUser)
      .innerJoin(portfolio, eq(portfolio.id, portfolioUser.portfolio_id))
      .where(eq(portfolioUser.id, user.id))
      .orderBy(portfolio.name)
  }
)

// Every Current Portfolio Entry, with its File's Assessment.
export const loadTerminalPortfolio = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => portfolioInput.parse(data))
  .handler(async ({ data }): Promise<TerminalPortfolioRow[]> => {
    const { portfolioId } = await assertPortfolioAccess(data)
    return readPortfolioRows(portfolioId)
  })

// One Company's File, by its slug; null when the Company has no Current
// Portfolio Entry in this Portfolio or no File.
export const loadTerminalCompanyFile = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => companyFileInput.parse(data))
  .handler(async ({ data }): Promise<TerminalFileView | null> => {
    const { portfolioId } = await assertPortfolioAccess(data)
    return readCompanyFile(portfolioId, data.slug)
  })

// The active Detections across the Portfolio's Files, newest first.
export const loadTerminalFeed = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => portfolioInput.parse(data))
  .handler(async ({ data }): Promise<TerminalFeedDetection[]> => {
    const { portfolioId } = await assertPortfolioAccess(data)
    return readPortfolioFeed(portfolioId)
  })

// Starts monitoring one Company from its File through
// `setPortfolioEntryMonitoring`: every Current Portfolio Entry of the Company
// becomes monitored, with the buyer's approval date. One-way: the customer Terminal never stops monitoring.
export const startTerminalMonitoring = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        portfolioId: z.string().uuid(),
        companyId: z.string().uuid(),
        approvedOn: approvedOnSchema,
      })
      .parse(data)
  )
  .handler(async ({ data }): Promise<void> => {
    const { portfolioId } = await assertPortfolioAccess(data)
    const entries = (await getPortfolioDelivery(portfolioId)).filter(
      ({ companyId }) => companyId === data.companyId
    )
    if (entries.length === 0)
      throw new Error("No Current Portfolio Entry for this Company")
    // Every Current Portfolio Entry of the Company, so the one the reader
    // shows always carries the buyer's choice.
    for (const { portfolioEntryId } of entries)
      await setPortfolioEntryMonitoring({
        portfolioId,
        portfolioEntryId,
        isMonitoring: true,
        approval: { approvedOn: data.approvedOn },
      })
  })

// The Portfolio's File Requests: Needs clarification, then In progress, then
// Published, each newest first. A request is Published once its linked
// Company has a Delivered File here.
export const loadTerminalFileRequests = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => portfolioInput.parse(data))
  .handler(async ({ data }): Promise<TerminalFileRequest[]> => {
    const { portfolioId } = await assertPortfolioAccess(data)
    const requests = await readFileRequests(portfolioId)
    return sortBy(
      requests.map((request) =>
        pick(request, [
          "id",
          "legalName",
          "service",
          "createdAt",
          "status",
          "requestedByEmail",
          "company",
        ])
      ),
      // Stable, so each group keeps the newest-first order.
      ({ status }) => dataByFileRequestStatus[status].order
    )
  })

// Records a member's request for a Company's File in the Portfolio.
export const requestTerminalFile = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({ portfolioSlug: z.string().min(1), fields: z.unknown() })
      .transform(({ portfolioSlug, fields }) => ({
        portfolioSlug,
        fields: fileRequestFieldsSchema.parse(fields),
      }))
      .parse(data)
  )
  .handler(async ({ data: { portfolioSlug, fields } }): Promise<void> => {
    const { portfolioId, userId } = await assertPortfolioAccess({
      portfolioSlug,
    })
    await db.insert(terminalFileRequest).values({
      portfolio_id: portfolioId,
      requested_by: userId,
      legal_name: fields.legalName,
      website: fields.website,
      street_address: fields.streetAddress,
      dba: fields.dba,
      service: fields.service,
      approved_on: fields.approvedOn,
    })
    // The ping is best-effort: a request is recorded whether or not Slack
    // answers, and a server without a webhook skips it.
    if (process.env.SLACK_WEBHOOK_URL)
      try {
        const { sendSlackFileRequestNotification } =
          await import("@/lib/slack-client")
        await sendSlackFileRequestNotification({ portfolioSlug, ...fields })
      } catch (error) {
        console.error("Error sending Slack notification:", error)
      }
  })

const placesInput = portfolioInput.extend({ sessionToken: z.string().uuid() })

// US addresses and businesses matching what the buyer has typed, for the
// Requests form's street address.
export const suggestTerminalAddresses = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    placesInput.extend({ query: z.string().trim().min(3).max(200) }).parse(data)
  )
  .handler(async ({ data }) => {
    await assertPortfolioAccess(data)
    return suggestPlaces(data.query, data.sessionToken)
  })

// One suggestion's full street address.
export const resolveTerminalAddress = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    placesInput.extend({ placeId: z.string().min(1).max(500) }).parse(data)
  )
  .handler(async ({ data }) => {
    await assertPortfolioAccess(data)
    return resolvePlaceStreetAddress(data.placeId, data.sessionToken)
  })

// Companies per Export Files page: about 45 rows each, so a page stays far
// below the host's response limit however large the book.
const exportPageSize = 50

// One page of Export Files (D34): the rows of every File on the
// chosen lists, by company name, and the number of Files across all pages.
// The window pages through the book and assembles the download. File links
// point at the customer's domain, wherever the export runs.
export const readTerminalExportPage = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    portfolioInput
      .extend({
        lists: z.array(z.nativeEnum(PortfolioCompanyStatus)).min(1),
        offset: z.number().int().min(0),
        timeZone: z.string().min(1),
      })
      .parse(data)
  )
  .handler(async ({ data }) => {
    const { portfolioId } = await assertPortfolioAccess(data)
    const { files, total } = await readPortfolioFiles(portfolioId, data.lists, {
      offset: data.offset,
      limit: exportPageSize,
    })
    return {
      rows: files.flatMap((file) =>
        buildExportRows(file, {
          siteUrl: NIGHTSHIFT_ORIGIN,
          portfolioSlug: data.portfolioSlug,
          timeZone: data.timeZone,
        })
      ),
      fileCount: files.length,
      total,
    }
  })
